import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import DeactivatedTag from "@/components/tags/DeactivatedTag";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import {
  formatLocaleCurrency,
  formatLocaleDate,
  formatLocaleNumber,
  formatLocalePercentageNumber,
} from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { tBoolean } from "@/utils/translationUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import {
  Card,
  Collapse,
  Descriptions,
  DescriptionsProps,
  Divider,
  Popconfirm,
  Table,
  TablePaginationConfig,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteProduct, useDownloadProductsExport, useFilterProducts } from "../../../../common/api/queries";
import { QueryKeys, TableSizes } from "../../../../common/constants";
import { ExportFileType } from "../../../../common/enums";
import { TabKeyRequest } from "../../../../common/types";
import type { UUID } from "../../../../typings/global";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { CommissionKind } from "../../../commissions/batch/enums";
import { BusinessCoefficientsDescriptions } from "../../../commissions/productcommissionssettings/components/BusinessCoefficientsDescriptions";
import { CommissionDivisionType } from "../../../commissions/productcommissionssettings/enum";
import { ProductCommissionsSettings } from "../../../commissions/productcommissionssettings/types";
import { getProductCommissionsSettingsLabel } from "../../../commissions/productcommissionssettings/utils";
import { DEFAULT_FILTER_PRODUCTS_QUERY_PARAMS } from "../../api";
import { Product, ProductFilterPageRequest } from "../../types";
import { PRODUCT_PAGE_TABS } from "../../utils";

const getLabelForValueCoveredByNbs = ({ value, isSFAPlus }: { value: boolean; isSFAPlus: boolean }, t: TFunction) => {
  if (isSFAPlus) {
    return value ? t("product.helpers.productProfiPlus") : t("product.helpers.productProjectsPlus");
  }

  return value ? t("product.helpers.productCoveredByNbs") : t("product.helpers.productNoCoveredByNbs");
};

type AdditionalInfoProps = {
  product: Product;
};

const AdditionalInfo = ({ product }: AdditionalInfoProps) => {
  const { t } = useTranslation();

  const isCurrentAgentTypePlus = useIsCurrentAgentTypePlus();

  return (
    <Descriptions
      size="small"
      title={t("product.titles.additionalInfo")}
      items={[
        {
          key: "coveredByNbs",
          label: t("product.attrs.coveredByNbs"),
          children: getLabelForValueCoveredByNbs({ value: product.coveredByNbs, isSFAPlus: isCurrentAgentTypePlus }, t),
        },
        {
          key: "subjectToVat",
          label: t("product.attrs.subjectToVat"),
          children: tBoolean(product.subjectToVat),
        },
        product.ibisProductIds
          ? {
              key: "ibis",
              label: "IBIS",
              children: (
                <Descriptions
                  size="small"
                  bordered
                  column={1}
                  styles={{ root: { maxWidth: 400 }, content: { textAlign: "right" } }}
                  items={[
                    {
                      key: "ibisProductIds",
                      label: t("product.attrs.ibisProductIds"),
                      children: product.ibisProductIds,
                    },
                    {
                      key: "ibisCommodityWeight",
                      label: t("product.attrs.ibisCommodityWeight"),
                      children: isDefined(product.ibisCommodityWeight)
                        ? `${formatLocaleNumber(product.ibisCommodityWeight, 2)} g`
                        : undefined,
                    },
                    {
                      key: "ibisCommodityShape",
                      label: t("contract.enums.commodityShape._label"),
                      children: t(`contract.enums.commodityShape.${product.ibisCommodityShape}`),
                    },
                  ]}
                />
              ),
            }
          : undefined,
      ].filter((item) => !!item)}
      bordered
      column={2}
    />
  );
};

type ExpandedRowProps = {
  product: Product;
};

const ExpandedRow = ({ product }: ExpandedRowProps) => {
  const { t } = useTranslation();

  if (!product.commissionsSettings || product.commissionsSettings.length === 0) {
    return <AdditionalInfo product={product} />;
  }

  const groupedByCommissionKind = product.commissionsSettings.reduce(
    (acc, item) => {
      const key = item.commissionKind;

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(item);
      return acc;
    },
    {} as Record<CommissionKind, (typeof product.commissionsSettings)[number][]>,
  );

  return (
    <div style={{ overflow: "auto" }} key={product.id}>
      <AdditionalInfo product={product} />

      <div className="margin-top-small">
        <h3 style={{ fontWeight: 600 }} className="margin-bottom-small">
          {t("commissions.productCommissionsSettings.sections.commissionSettings")}
        </h3>

        <div style={{ overflowY: "auto", maxHeight: 600 }}>
          {Object.entries(groupedByCommissionKind).map(([key, commissionsSettings], index) => {
            const collapseItems = commissionsSettings.map((item) => ({
              key: item.id,
              label: getProductCommissionsSettingsLabel(item, t),
              children: <SettingsData commissionsSettings={item} />,
            }));

            return (
              <div key={key}>
                <h4 style={{ marginBottom: 8, marginTop: index === 0 ? 4 : 16 }}>
                  {`${t(`commissions.batch.enums.commissionKind.${key}`)}`}
                </h4>

                <Collapse items={collapseItems} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

type SettingsDataProps = {
  commissionsSettings: ProductCommissionsSettings;
};

const SettingsData = ({ commissionsSettings }: SettingsDataProps) => {
  const { t } = useTranslation();

  const {
    divisionType,
    year1PaymentRatio,
    year2PaymentRatio,
    year3PaymentRatio,
    directPointsAmount,
    directPaymentAmount,
    directPaymentRatio,
    omitManagerialCommissions,
    startDate,
    endDate,
    amountAdjustmentRatio,
    pointsCoefficient,
    businessCoefficients,
  } = commissionsSettings;

  const settingsItems: DescriptionsProps["items"] = [
    {
      key: `startDate`,
      label: t(`commissions.productCommissionsSettings.attrs.startDate`),
      children: formatLocaleDate(startDate),
    },
    {
      key: `endDate`,
      label: t(`commissions.productCommissionsSettings.attrs.endDate`),
      children: endDate ? formatLocaleDate(endDate) : "-",
    },
    {
      key: `amountAdjustmentRatio`,
      label: (
        <LabelWithTooltip
          tooltip={t("commissions.productCommissionsSettings.helpers.amountAdjustmentRatioHint")}
          label={t("commissions.productCommissionsSettings.attrs.amountAdjustmentRatio")}
        />
      ),
      children: formatLocalePercentageNumber(amountAdjustmentRatio, 4),
    },
    {
      key: `pointsCoefficient`,
      label: t(`commissions.productCommissionsSettings.attrs.pointsCoefficient`),
      children: <span>{formatLocaleNumber(pointsCoefficient, 2)}</span>,
    },
    {
      key: "divisionType",
      label: t("commissions.productCommissionsSettings.enums.commissionDivisionType._label"),
      children: t(`commissions.productCommissionsSettings.enums.commissionDivisionType.${divisionType}`),
      span: divisionType === CommissionDivisionType.NONE ? 4 : 1,
    },
  ];

  if (divisionType !== CommissionDivisionType.NONE) {
    settingsItems.push(
      {
        key: "year1PaymentRatio",
        label: t("commissions.productCommissionsSettings.attrs.year1PaymentRatio"),
        children: isDefined(year1PaymentRatio) ? formatLocalePercentageNumber(year1PaymentRatio) : undefined,
      },
      {
        key: "year2PaymentRatio",
        label: t("commissions.productCommissionsSettings.attrs.year2PaymentRatio"),
        children: isDefined(year2PaymentRatio) ? formatLocalePercentageNumber(year2PaymentRatio) : undefined,
        span: divisionType === CommissionDivisionType.THREE_YEARS ? 1 : 2,
      },
    );
  }

  if (divisionType === CommissionDivisionType.THREE_YEARS) {
    settingsItems.push({
      key: "year3PaymentRatio",
      label: t("commissions.productCommissionsSettings.attrs.year3PaymentRatio"),
      children: isDefined(year3PaymentRatio) ? formatLocalePercentageNumber(year3PaymentRatio) : undefined,
    });
  }

  settingsItems.push(
    {
      key: "directPointsAmount",
      label: (
        <LabelWithTooltip
          tooltip={t("commissions.productCommissionsSettings.helpers.directPointsAmountHint")}
          label={t("commissions.productCommissionsSettings.attrs.directPointsAmount")}
        />
      ),
      children: isDefined(directPointsAmount) ? formatLocaleNumber(directPointsAmount, 2) : "-",
    },
    {
      key: "directPaymentAmount",
      label: (
        <LabelWithTooltip
          tooltip={t("commissions.productCommissionsSettings.helpers.directPaymentAmountHint")}
          label={t("commissions.productCommissionsSettings.attrs.directPaymentAmount")}
        />
      ),
      children: isDefined(directPaymentAmount) ? formatLocaleCurrency(directPaymentAmount) : "-",
    },
    {
      key: "directPaymentRatio",
      label: (
        <LabelWithTooltip
          tooltip={t("commissions.productCommissionsSettings.helpers.directPaymentRatioHint")}
          label={t("commissions.productCommissionsSettings.attrs.directPaymentRatio")}
        />
      ),
      children: isDefined(directPaymentRatio) ? formatLocalePercentageNumber(directPaymentRatio, 4) : "-",
    },
    {
      key: "omitManagerialCommissions",
      label: t("commissions.productCommissionsSettings.attrs.omitManagerialCommissions"),
      children: tBoolean(omitManagerialCommissions),
    },
  );

  settingsItems.push({
    key: "businessCoefficients",
    label: t(`commissions.productCommissionsSettings.attrs.businessCoefficients._label`),
    children: <BusinessCoefficientsDescriptions businessCoefficients={businessCoefficients} />,
    span: 4,
  });

  return (
    <div>
      <Descriptions
        style={{ overflowX: "auto", display: "grid" }}
        styles={{
          title: { fontSize: 14 },
          label: { whiteSpace: "nowrap", padding: "8px 12px" },
          content: { whiteSpace: "nowrap" },
        }}
        size="small"
        bordered
        items={settingsItems}
        column={4}
      />
    </div>
  );
};

type Props = {
  onUpdateClick: (product: Product) => void;
};

export const ProductTableView = ({ onUpdateClick }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { tab, ...filterSearchParams } = useFilterSearchParams<ProductFilterPageRequest & TabKeyRequest>({
    params: ["sectors", "groupIds", "institutionIds", QueryKeys.TAB],
    defaultQueryParamsObject: { ...DEFAULT_FILTER_PRODUCTS_QUERY_PARAMS, [QueryKeys.TAB]: PRODUCT_PAGE_TABS.PRODUCTS },
  });

  const { data, isLoading } = useFilterProducts(filterSearchParams, tab === PRODUCT_PAGE_TABS.PRODUCTS);
  const deleteProduct = useDeleteProduct();
  const downloadProductsExport = useDownloadProductsExport();

  const columns: ColumnsType<Product> = [
    {
      key: "group",
      title: t("product.attrs.productGroupId"),
      width: 160,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.productGroup.name}</Ellipsis>,
    },
    {
      key: "name",
      title: t("product.helpers.productName"),
      width: 190,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <>
          <Ellipsis>{record.name}</Ellipsis>
          {record.deactivated && (
            <>
              <br />
              <DeactivatedTag style={{ marginRight: 0 }} />
            </>
          )}
        </>
      ),
    },
    {
      key: "sector",
      title: t("product.enums.financialSector._label"),
      width: 150,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{t("product.enums.financialSector." + record.sector)}</Ellipsis>,
    },
    {
      key: "insuranceType",
      title: (
        <>
          <span>{t("product.helpers.insuranceTypeLabel")}</span>
          <br />
          <i>{t("product.attrs.tariffGroupId")}</i>
        </>
      ),
      width: 130,
      ellipsis: { showTitle: false },
      render: (_, record) =>
        record.insuranceType && (
          <>
            <Ellipsis>{t("contract.enums.insuranceType." + record.insuranceType)}</Ellipsis>
            {record.tariffGroup && (
              <>
                <br />
                <Ellipsis style={{ fontStyle: "italic" }}>{record.tariffGroup.name}</Ellipsis>
              </>
            )}
          </>
        ),
    },
    {
      key: "institutions",
      title: t("common.institutions"),
      width: 290,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <Ellipsis>{record.institutions.map((institution) => institution.name).join(", ")}</Ellipsis>
      ),
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <div className="nowrap">
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("product.helpers.productDeleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => handleDeleteProduct(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  const handleDeleteProduct = async (id: UUID) => {
    await deleteProduct.mutateAsync(id);
  };

  return (
    <Card className="card-box">
      <div className={data?.totalElementsCount ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() =>
            downloadProductsExport.mutate({
              exportFileType: ExportFileType.XLSX,
              ...filterSearchParams,
            })
          }
        />

        <Divider orientation="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() =>
            downloadProductsExport.mutate({
              exportFileType: ExportFileType.CSV,
              ...filterSearchParams,
            })
          }
        />
      </div>

      <Table<Product>
        {...tableStandardProps()}
        loading={isLoading}
        columns={columns}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={data?.pageData}
        pagination={{
          ...paginationTableProps,
          ...data,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={handleTableChange}
        expandable={{
          expandedRowRender: (product) => <ExpandedRow product={product} />,
        }}
      />
    </Card>
  );
};
